/** Step 3's first choice, as two cards: run an agent now, or score the outputs the dataset holds. */

import { Bot, Database } from "lucide-react";
import { useDatasetSource } from "@/features/datasets/hooks";
import { formatCount } from "@/lib/format";
import { ChoiceCard } from "./choice-card";
import type { NewExperiment } from "./use-new-experiment";

/** Where a live run's traces come from, as the agent card says it. */
function liveSource(flow: NewExperiment, limit: number | undefined): string {
  const { version, next, latest } = flow.liveVersion;
  if (version != null) return `Take v${version}'s traces`;
  if (next) return `Pull the next ${limit ? formatCount(limit) : "traces"} after v${latest}`;
  return "Pull the period's traces";
}

export function RunnerChoice({ flow, locked }: { flow: NewExperiment; locked: boolean }) {
  const limit = useDatasetSource(flow.datasetId ?? "", flow.live).data?.limit;
  const outputsNote = flow.live
    ? "The traces' own outputs, scored as they ran in production."
    : flow.hasOutputs
      ? "Score the dataset's recorded outputs. No agent is called."
      : "This dataset has no outputs.";
  return (
    <fieldset className="grid gap-2.5 sm:grid-cols-2">
      <legend className="sr-only">Who answers each item?</legend>
      <ChoiceCard
        name="ne-runner"
        icon={<Bot className="size-4" />}
        checked={flow.runner === "agent"}
        disabled={locked}
        onPick={() => flow.form.setRunner("agent")}
        note={
          flow.live
            ? `${liveSource(flow, limit)}, then call an agent on their inputs and score its answers.`
            : "Call an agent on every item, then score its answers."
        }
      >
        Run an agent
      </ChoiceCard>
      <ChoiceCard
        name="ne-runner"
        icon={<Database className="size-4" />}
        checked={flow.runner === "outputs"}
        disabled={!flow.hasOutputs || locked}
        onPick={() => flow.form.setRunner("outputs")}
        note={outputsNote}
      >
        Use the outputs in the dataset
      </ChoiceCard>
    </fieldset>
  );
}
